<template>
  <transition name="wc-confirm-fade">
    <div v-if="visible" class="wc-confirm-backdrop" @mousedown.self="onBackdrop">
      <transition name="wc-confirm-pop">
        <div v-if="visible" class="wc-confirm-card" role="dialog" :aria-labelledby="uid + '-title'" :aria-describedby="uid + '-msg'">

          <!-- Icono -->
          <div class="wc-confirm-icon" :class="'wc-confirm-icon--' + variant">
            <!-- danger / warning -->
            <svg v-if="variant === 'danger' || variant === 'warning'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
              <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
            </svg>
            <!-- info -->
            <svg v-else-if="variant === 'info'" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/>
            </svg>
            <!-- success -->
            <svg v-else width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>

          <!-- Texto -->
          <div class="wc-confirm-body">
            <h6 :id="uid + '-title'" class="wc-confirm-title">{{ title }}</h6>
            <p  :id="uid + '-msg'"   class="wc-confirm-msg"   v-if="message">{{ message }}</p>
          </div>

          <!-- Acciones -->
          <div class="wc-confirm-actions">
            <!-- Con cancelText en null el diálogo queda de UN solo botón y sirve de
                 aviso ($wcAlert), sin tener que mantener otro componente para eso. -->
            <button
              v-if="cancelText"
              class="wc-confirm-btn wc-confirm-btn--cancel"
              @click="cancel"
              :disabled="loading"
            >
              {{ cancelText }}
            </button>
            <button
              class="wc-confirm-btn"
              :class="'wc-confirm-btn--' + variant"
              @click="confirm"
              :disabled="loading"
            >
              <span v-if="loading" class="wc-confirm-spinner" />
              <span v-else>{{ confirmText }}</span>
            </button>
          </div>

        </div>
      </transition>
    </div>
  </transition>
</template>

<script>
let _uid = 0

export default {
  name: 'ConfirmDialog',

  data() {
    return {
      uid:         'wc-confirm-' + (++_uid),
      visible:     false,
      loading:     false,
      // Opciones configurables por llamada
      title:       this.$t('layoutsMisc.confirm.defaultTitle'),
      message:     '',
      confirmText: this.$t('layoutsMisc.confirm.confirm'),
      cancelText:  this.$t('layoutsMisc.confirm.cancel'),
      variant:     'danger',   // danger | warning | info | success
      // Resolvers de la Promise
      _resolve:    null,
      _reject:     null,
    }
  },

  mounted() {
    // ESC para cancelar
    window.addEventListener('keydown', this._onKeydown)
  },

  beforeDestroy() {
    window.removeEventListener('keydown', this._onKeydown)
  },

  methods: {
    /**
     * Abre el diálogo y devuelve una Promise.
     * Se resuelve con true al confirmar, false al cancelar.
     *
     * @param {Object} opts
     * @param {string} opts.title
     * @param {string} [opts.message]
     * @param {string} [opts.confirmText]
     * @param {string} [opts.cancelText]
     * @param {'danger'|'warning'|'info'|'success'} [opts.variant]
     */
    open(opts = {}) {
      this.title       = opts.title       ?? this.$t('layoutsMisc.confirm.defaultTitle')
      this.message     = opts.message     ?? ''
      this.confirmText = opts.confirmText ?? this.$t('layoutsMisc.confirm.confirm')
      this.cancelText  = opts.cancelText  ?? this.$t('layoutsMisc.confirm.cancel')
      this.variant     = opts.variant     ?? 'danger'
      this.loading     = false
      this.visible     = true

      return new Promise((resolve, reject) => {
        this._resolve = resolve
        this._reject  = reject
      })
    },

    confirm() {
      this._resolve?.(true)
      this._close()
    },

    cancel() {
      this._resolve?.(false)
      this._close()
    },

    onBackdrop() {
      this.cancel()
    },

    _close() {
      this.visible  = false
      this.loading  = false
      this._resolve = null
      this._reject  = null
    },

    _onKeydown(e) {
      if (this.visible && e.key === 'Escape') this.cancel()
    },
  },
}
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Inter:wght@400;500&display=swap");

/* ── Backdrop ───────────────────────────────────────────── */
.wc-confirm-backdrop {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: rgba(10, 15, 30, 0.38);
  backdrop-filter: blur(3px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
}

/* ── Card ───────────────────────────────────────────────── */
.wc-confirm-card {
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18), 0 4px 16px rgba(0, 0, 0, 0.08);
  width: 100%;
  max-width: 380px;
  padding: 28px 28px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0;
  font-family: 'DM Sans', system-ui, sans-serif;
  text-align: center;
}

/* ── Icono ──────────────────────────────────────────────── */
.wc-confirm-icon {
  width: 52px;
  height: 52px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
  flex-shrink: 0;
}
.wc-confirm-icon--danger  { background: #fef2f2; color: #dc2626; }
.wc-confirm-icon--warning { background: #fffbeb; color: #d97706; }
.wc-confirm-icon--info    { background: #eff6ff; color: #2563eb; }
.wc-confirm-icon--success { background: #f0fdf4; color: #16a34a; }

/* ── Body ───────────────────────────────────────────────── */
.wc-confirm-body {
  width: 100%;
  margin-bottom: 22px;
}

.wc-confirm-title {
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 16px;
  font-weight: 700;
  color: #111827;
  margin: 0 0 6px;
  line-height: 1.35;
}

.wc-confirm-msg {
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 13.5px;
  color: #6b7280;
  margin: 0;
  line-height: 1.55;
}

/* ── Acciones ───────────────────────────────────────────── */
.wc-confirm-actions {
  display: flex;
  gap: 10px;
  width: 100%;
}

.wc-confirm-btn {
  flex: 1;
  height: 40px;
  border: none;
  border-radius: 10px;
  font-family: 'DM Sans', system-ui, sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s, opacity 0.15s, transform 0.1s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  outline: none;
}
.wc-confirm-btn:active { transform: scale(0.97); }
.wc-confirm-btn:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

/* Cancel */
.wc-confirm-btn--cancel {
  background: #f3f4f6;
  color: #374151;
}
.wc-confirm-btn--cancel:hover:not(:disabled) { background: #e5e7eb; }

/* Danger */
.wc-confirm-btn--danger {
  background: #dc2626;
  color: #fff;
}
.wc-confirm-btn--danger:hover:not(:disabled) { background: #b91c1c; }

/* Warning */
.wc-confirm-btn--warning {
  background: #d97706;
  color: #fff;
}
.wc-confirm-btn--warning:hover:not(:disabled) { background: #b45309; }

/* Info */
.wc-confirm-btn--info {
  background: #2563eb;
  color: #fff;
}
.wc-confirm-btn--info:hover:not(:disabled) { background: #1d4ed8; }

/* Success */
.wc-confirm-btn--success {
  background: #16a34a;
  color: #fff;
}
.wc-confirm-btn--success:hover:not(:disabled) { background: #15803d; }

/* ── Spinner inline ─────────────────────────────────────── */
.wc-confirm-spinner {
  width: 15px;
  height: 15px;
  border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff;
  border-radius: 50%;
  animation: wc-spin 0.6s linear infinite;
  display: inline-block;
}
@keyframes wc-spin { to { transform: rotate(360deg); } }

/* ── Transiciones ───────────────────────────────────────── */
.wc-confirm-fade-enter-active,
.wc-confirm-fade-leave-active { transition: opacity 0.2s ease; }
.wc-confirm-fade-enter,
.wc-confirm-fade-leave-to     { opacity: 0; }

.wc-confirm-pop-enter-active  { transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease; }
.wc-confirm-pop-leave-active  { transition: transform 0.15s ease, opacity 0.15s ease; }
.wc-confirm-pop-enter         { transform: scale(0.88) translateY(8px); opacity: 0; }
.wc-confirm-pop-leave-to      { transform: scale(0.94) translateY(4px); opacity: 0; }
</style>
